import React, { useContext, useEffect, useState, useRef } from 'react';
import { Auth } from '@aws-amplify/auth';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Box, Text, Space, Stack } from '@tonic-ui/react';
import { FormContainer, InputField, mfaLoginInput, SubmitButton } from '../../components/forms';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import filterMessage from '../../utils/message-filter';
import { gotoSignIn } from '../../utils/helpers';
import RouteLink from '../../components/route-link';
import { isCognitoUserError } from '../../services/helpers';
import OverlayLoader from '../../components/loader/overlay-loader';
import CenteredLayout from '../../components/centered-layout';

interface IProps {
  auth: Record<string, any>;
  confirmationCode: string;
}

const MfaLogin = (): JSX.Element => {
  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { cognitoUser, setAuthStatus, eventDistributor } = useContext(AuthContext);
  const focusRef = useRef(null);

  useEffect(() => {
    if (Object.keys(cognitoUser).length === 0) {
      // use-case: user circumvents login process by navigating to /mfa-login via url bar/bookmark
      gotoSignIn(eventDistributor);
    }
  }, []);

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current = focusRef.current.focus();
    }
  }, []);

  const handleSubmit = async ({
    confirmationCode,
    setSubmitting,
    resetForm,
    mfaType = 'SOFTWARE_TOKEN_MFA',
  }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    setIsLoading(true);
    try {
      if (cognitoUser && confirmationCode && mfaType) {
        const mfaLoggedUser = await Auth.confirmSignIn(cognitoUser, confirmationCode, mfaType);

        if (mfaLoggedUser) {
          setAuthStatus(mfaLoggedUser);
        }
      }
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errMsg = !error?.message ? error : error.message;
      console.error('Error: ', errMsg);

      // Check if session expired. No way to continue except by user reentering login
      if (isCognitoUserError(error) && error.code === 'NotAuthorizedException') {
        return eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: '/' });
      }

      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });
      setIsLoading(false);
      setSubmitting(false);
      resetForm();
    }
  };

  const defaultValues: IProps = {
    auth: {},
    confirmationCode: '',
  };

  return (
    <>
      {isLoading ? (
        <OverlayLoader isLoading={isLoading} />
      ) : (
        <CenteredLayout>
          <FormContainer
            id="mfa-login-form"
            customValidation={mfaLoginInput}
            initialValues={defaultValues}
            submitAction={(formPayload) => handleSubmit(formPayload)}
          >
            <Stack>
              <Text size="2xl" linHeight="base" marginBottom="6x">
                {t('mfa.title')}
              </Text>
              {formErrors?.error && (
                <Box marginBottom="4x">
                  <NotificationBanner
                    name="mfa-login-form"
                    severity="error"
                    variant="outline"
                    shouldOpen={true}
                    bannerText={formErrors.error}
                  />
                </Box>
              )}
              <InputField
                id="one-time-password"
                type="text"
                label={t('mfaLogin.tokenInput')}
                name="confirmationCode"
                isRequired={true}
                ref={focusRef}
                role="textbox"
                maxLength={36}
              />
              <RouteLink href="/mfa-reset" marginTop="2x">
                {t('mfaLogin.needReset')}
              </RouteLink>
              <Space height="12x" />
              <SubmitButton label={t('continue')} id="mfa-login-submit-btn" />
              <Box textAlign="center" mt={32}>
                <RouteLink href="/">{t('goBack')}</RouteLink>
              </Box>
            </Stack>
          </FormContainer>
        </CenteredLayout>
      )}
    </>
  );
};

export default MfaLogin;
